import type { ApplicationFeatureType } from '@core/auth/permissions';
import type { IconDefinition } from '@fortawesome/angular-fontawesome';

/**
 * Options available when creating a menuitem for the sidebar
 *
 * @see {@link MenuItem}
 * @see {@link withMenuItem}
 */
export type MenuItemOptions = {
  label: string;
  icon?: IconDefinition;
  children?: MenuItem[];
  features?: ApplicationFeatureType[];
  routerLink?: string;
  href?: string;
  target?: string;
  external?: boolean;
};

/**
 * A header or link in for the sidebar
 */
export type MenuItem = {
  label: string;
  icon?: IconDefinition;
  children: MenuItem[];
  features: Set<ApplicationFeatureType>;
  routerLink?: string;
  href?: string;
  target?: string;
  external?: boolean;
};

/**
 * Helper to create a MenuItem from MenuItemOptions
 */
export const withMenuItem = (options: MenuItemOptions): MenuItem => {
  return {
    ...options,
    children: options.children ?? [],
    features: new Set(options.features ?? [])
  };
};

/**
 * Helper to compose multiple MenuItems into an array
 */
export const composeMenuItems = (...items: MenuItem[]): MenuItem[] => {
  return items;
};

/**
 * Creates a deep copy of the menu items and filters the visible menu items based on user permissions
 */
export const filterMenuItems = (menuItems: MenuItem[], userPermissions: Set<ApplicationFeatureType>): MenuItem[] => {
  return menuItems
    .filter(item => item.features.size === 0 || item.features.intersection(userPermissions).size > 0)
    .map(item => {
      const children = filterMenuItems(item.children, userPermissions);

      return {
        ...item,
        children
      };
    })
    .filter(item => item.children.length > 0 || item.routerLink != null || item.href != null);
};
